import { defineStore } from 'pinia';
import _ from 'lodash';
import { getAuthFlowApi } from '@Api/authFlow.api';
import type { FlowStep, FlowTask } from '@Shared';
import { FLOW_DEFINITIONS } from '@Constants/authFlow.constant';
import { AuthFlowType } from '@Enums/authFlowType.enum';
import { isAuthFlowType } from '@Constants/authFlow.constant'; 

export const useAuthFlowStore = defineStore('authFlow', {
    state: () => ({
        authFlow: '',
        currentRoute: null as string | null,
        flowSteps: [] as FlowTask[],
        // True when the most recent _getRequiredStepsFromFlow call (from either
        // loadAuthFlow or addToAuthFlow) received a non-empty rawFlows payload
        // but every stepName was unrecognized — i.e. a step-up was requested
        // but nothing survived filtering. Distinct from an empty payload
        // (nothing was requested), which leaves this false. Only meaningful
        // immediately after an addToAuthFlow call — checkForAdditionalFlows is
        // the sole reader, and it always calls addToAuthFlow first. Reset by
        // _clearFlowSequence so a stale true can't leak into an unrelated read.
        hasUnrecognizedOnly: false,
        // Which display of the DisableVpn step-up this is (1st, 2nd, or 3rd),
        // driving the copy variant on the disable-vpn view. Defaults to the
        // first-display copy. The view's display report returns
        // the authoritative ordinal from the backend and populates this via
        // setVpnStepUpDisplayOrdinal; if the report fails, the current value
        // stands.
        vpnStepUpDisplayOrdinal: 1
    }),
    actions: {
        async loadAuthFlow(authFlow: string) {
            this.authFlow = authFlow;

            const requiredFlows = await getAuthFlowApi(authFlow);
            const mappedFlows = this._getRequiredStepsFromFlow(requiredFlows);

            this._clearFlowSequence();
            this._buildFlowSequence(mappedFlows); 

            console.log('Computed Flow Map:', this.flowSteps);
        },
        addToAuthFlow(rawFlows: FlowStep[]){
            const mappedFlows = this._getRequiredStepsFromFlow(rawFlows);
            this._buildFlowSequence(mappedFlows); 

            console.log('Computed Flow Map:', this.flowSteps);
        },
        _getRequiredStepsFromFlow(rawFlows: FlowStep[]): AuthFlowType[] {
            const steps = _.map(rawFlows, 'stepName');
            const validFlows = _.filter(steps, isAuthFlowType);
            const invalidFlows = _.reject(steps, isAuthFlowType);
        
            if (!_.isEmpty(invalidFlows)) {
                console.warn("Received unknown auth flows from API:", invalidFlows);
            }

            this.hasUnrecognizedOnly = !_.isEmpty(steps) && _.isEmpty(validFlows);

            return validFlows
        },

        _buildFlowSequence(requiredFlows: AuthFlowType[]) {
            for (const flow of requiredFlows) {
                const tasks = FLOW_DEFINITIONS[flow];
                this.flowSteps.push(...tasks);
            }
        },

        _clearFlowSequence() {
            this.flowSteps = [];
            this.hasUnrecognizedOnly = false;
        },

        getNextFlowTask():FlowTask | null {
            const currentIndex = _.findIndex(this.flowSteps, { routeName: this.currentRoute ?? undefined });
            const nextFlowTask = _.get(this.flowSteps, currentIndex + 1, null);
            this.currentRoute = nextFlowTask?.routeName ?? null;

            return nextFlowTask;
        },

        getPreviousFlowTask():FlowTask | null {
            const currentIndex = _.findIndex(this.flowSteps, { routeName: this.currentRoute ?? undefined });
            const prevFlowTask = _.get(this.flowSteps, currentIndex - 1, null);
            this.currentRoute = prevFlowTask?.routeName ?? null;

            return prevFlowTask;
        },

        // The backend count only grows, so a lower attempt can only be a
        // stale response (e.g. a slow mount-time report resolving after a
        // re-display report) — ignore it rather than regressing the copy.
        setVpnStepUpDisplayOrdinal(ordinal: number) {
            if (ordinal > this.vpnStepUpDisplayOrdinal) {
                this.vpnStepUpDisplayOrdinal = ordinal;
            }
        },

        // The router guard persists the active step here as the flow cursor.
        // A view that clears the sequence to force a fresh evaluation must
        // also clear the cursor: if the backend re-emits the same step, a
        // stale cursor makes getNextFlowTask treat it as already current and
        // skip past it instead of showing it again.
        clearCurrentRoute() {
            this.currentRoute = null;
        },

        setCurrentRoute(routeName: string) {
            if (this.flowSteps.length === 0) {
                // Flow cleared — skip silently
                return;
            }
            const route = _.find(this.flowSteps, { 'routeName': routeName });
            if (!route) {
                console.warn(`[authFlow.store] setCurrentRoute: '${routeName}' not found in flowSteps`);
                return;
            }
            this.currentRoute = routeName;
        },
        
    },
});